import React, { useRef, useEffect, useState } from 'react';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [imgWidth, setImgWidth] = useState('');
  const [imgHeight, setImgHeight] = useState('');

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = () => {
    isInternalChange.current = true;
    if (onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const exec = (command, val = null) => {
    document.execCommand(command, false, val);
    handleInput();
  };

  const handleLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  const handleImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) exec('insertImage', url);
  };

  const getSelectedImg = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      let node = sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      while (node && node !== editorRef.current) {
        if (node.tagName === 'IMG') return node;
        node = node.parentElement;
      }
    }
    return null;
  };

  const handleEditorClick = (e) => {
    if (e.target.tagName === 'IMG') {
      const img = e.target;
      setSelectedImg(img);
      setImgWidth(img.style.width || img.naturalWidth || '');
      setImgHeight(img.style.height || img.naturalHeight || '');
    } else {
      setSelectedImg(null);
    }
  };

  const applyImgStyle = (prop, val) => {
    if (!selectedImg) return;
    selectedImg.style[prop] = val;
    handleInput();
  };

  const setImgSize = (prop, val) => {
    if (!selectedImg) return;
    const num = parseInt(val);
    if (isNaN(num) || num < 1) {
      selectedImg.style[prop] = '';
    } else {
      selectedImg.style[prop] = num + 'px';
    }
    handleInput();
  };

  const alignImg = (align) => {
    if (!selectedImg) return;
    selectedImg.style.float = '';
    selectedImg.style.marginLeft = '';
    selectedImg.style.marginRight = '';
    selectedImg.style.display = '';
    if (align === 'left') {
      selectedImg.style.float = 'left';
      selectedImg.style.marginRight = '12px';
      selectedImg.style.marginBottom = '6px';
    } else if (align === 'right') {
      selectedImg.style.float = 'right';
      selectedImg.style.marginLeft = '12px';
      selectedImg.style.marginBottom = '6px';
    } else if (align === 'center') {
      selectedImg.style.display = 'block';
      selectedImg.style.marginLeft = 'auto';
      selectedImg.style.marginRight = 'auto';
      selectedImg.style.float = '';
    }
    handleInput();
  };

  const resetImg = () => {
    if (!selectedImg) return;
    selectedImg.style.width = '';
    selectedImg.style.height = '';
    selectedImg.style.float = '';
    selectedImg.style.marginLeft = '';
    selectedImg.style.marginRight = '';
    selectedImg.style.display = '';
    setImgWidth(selectedImg.naturalWidth || '');
    setImgHeight(selectedImg.naturalHeight || '');
    handleInput();
  };

  const deleteImg = () => {
    if (!selectedImg) return;
    selectedImg.remove();
    setSelectedImg(null);
    handleInput();
  };

  const applyFontName = (font) => {
    exec('fontName', font);
  };

  const applyFontSize = (size) => {
    exec('fontSize', size);
  };

  const increaseFontSize = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      let parent = range.commonAncestorContainer;
      if (parent.nodeType === 3) parent = parent.parentElement;
      const currentSize = parent?.style?.fontSize;
      if (currentSize) {
        const px = parseInt(currentSize);
        exec('fontSize', '7');
        document.execCommand('fontSize', false, '7');
        const fonts = document.querySelectorAll('font[size="7"]');
        fonts.forEach(f => {
          f.removeAttribute('size');
          f.style.fontSize = (px + 2) + 'px';
        });
      } else {
        exec('fontSize', '5');
      }
    } else {
      exec('fontSize', '5');
    }
    handleInput();
  };

  const decreaseFontSize = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      let parent = range.commonAncestorContainer;
      if (parent.nodeType === 3) parent = parent.parentElement;
      const currentSize = parent?.style?.fontSize;
      if (currentSize) {
        const px = parseInt(currentSize);
        if (px > 10) {
          exec('fontSize', '7');
          document.execCommand('fontSize', false, '7');
          const fonts = document.querySelectorAll('font[size="7"]');
          fonts.forEach(f => {
            f.removeAttribute('size');
            f.style.fontSize = (px - 2) + 'px';
          });
        }
      } else {
        exec('fontSize', '2');
      }
    } else {
      exec('fontSize', '2');
    }
    handleInput();
  };

  const btn = (icon, title, action, isActive) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); action(); }}
      className={`px-2 py-1 rounded text-sm hover:bg-slate-200 transition ${isActive ? 'bg-slate-200' : ''}`}
      title={title}
    >
      {icon}
    </button>
  );

  return (
    <div className="rich-text-editor border border-slate-300 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-slate-200 bg-slate-50">
        {btn(<b>B</b>, 'Bold', () => exec('bold'))}
        {btn(<i>I</i>, 'Italic', () => exec('italic'))}
        {btn(<u>U</u>, 'Underline', () => exec('underline'))}
        {btn(<s>S</s>, 'Strikethrough', () => exec('strikeThrough'))}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { exec('formatBlock', e.target.value); e.target.selectedIndex = 0; }}
          className="text-xs border border-slate-300 rounded px-1 py-0.5 bg-white"
          defaultValue=""
        >
          <option value="">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="h5">Heading 5</option>
          <option value="h6">Heading 6</option>
          <option value="blockquote">Quote</option>
          <option value="pre">Code Block</option>
        </select>
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { applyFontName(e.target.value); e.target.selectedIndex = 0; }}
          className="text-xs border border-slate-300 rounded px-1 py-0.5 bg-white"
          defaultValue=""
        >
          <option value="">Font Family</option>
          <option value="Arial, Helvetica, sans-serif">Arial</option>
          <option value="'Courier New', Courier, monospace">Courier New</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Times New Roman', Times, serif">Times New Roman</option>
          <option value="'Trebuchet MS', Helvetica, sans-serif">Trebuchet MS</option>
          <option value="Verdana, Geneva, sans-serif">Verdana</option>
          <option value="'Segoe UI', Tahoma, sans-serif">Segoe UI</option>
          <option value="Roboto, sans-serif">Roboto</option>
          <option value="'Inter', sans-serif">Inter</option>
          <option value="Garamond, serif">Garamond</option>
          <option value="'Comic Sans MS', cursive">Comic Sans</option>
          <option value="Impact, sans-serif">Impact</option>
        </select>
        <div className="flex items-center gap-0.5">
          {btn('A−', 'Decrease Font Size', decreaseFontSize)}
          <select
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => { applyFontSize(e.target.value); e.target.selectedIndex = 4; }}
            className="text-xs border border-slate-300 rounded px-1 py-0.5 bg-white"
            defaultValue="3"
          >
            <option value="1">10px</option>
            <option value="2">12px</option>
            <option value="3">14px</option>
            <option value="4">16px</option>
            <option value="5">18px</option>
            <option value="6">24px</option>
            <option value="7">32px</option>
          </select>
          {btn('A+', 'Increase Font Size', increaseFontSize)}
        </div>
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const sel = window.getSelection();
            if (sel.rangeCount > 0) {
              let node = sel.anchorNode;
              if (node && node.nodeType === 3) node = node.parentElement;
              if (node) {
                node.style.lineHeight = e.target.value;
                handleInput();
              }
            }
            e.target.selectedIndex = 0;
          }}
          className="text-xs border border-slate-300 rounded px-1 py-0.5 bg-white"
          defaultValue=""
        >
          <option value="">Line Height</option>
          <option value="1">1.0</option>
          <option value="1.2">1.2</option>
          <option value="1.5">1.5</option>
          <option value="1.8">1.8</option>
          <option value="2.0">2.0</option>
          <option value="2.5">2.5</option>
          <option value="3.0">3.0</option>
        </select>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn('≡', 'Ordered List', () => exec('insertOrderedList'))}
        {btn('•', 'Bullet List', () => exec('insertUnorderedList'))}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn('⟸', 'Align Left', () => exec('justifyLeft'))}
        {btn('⇿', 'Align Center', () => exec('justifyCenter'))}
        {btn('⟹', 'Align Right', () => exec('justifyRight'))}
        {btn('☰', 'Justify', () => exec('justifyFull'))}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn('⟵', 'Outdent', () => exec('outdent'))}
        {btn('⟶', 'Indent', () => exec('indent'))}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <input
          type="color"
          onChange={(e) => exec('foreColor', e.target.value)}
          className="w-7 h-7 rounded cursor-pointer border border-slate-300"
          title="Text Color"
        />
        <input
          type="color"
          onChange={(e) => exec('hiliteColor', e.target.value)}
          className="w-7 h-7 rounded cursor-pointer border border-slate-300"
          title="Background Color"
        />
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn('🔗', 'Insert Link', handleLink)}
        {btn('🖼', 'Insert Image', handleImage)}
        {selectedImg && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-300">
            <span className="text-[10px] text-slate-500 font-semibold mr-1">IMG:</span>
            <input
              type="number"
              placeholder="W"
              value={imgWidth}
              onChange={(e) => { setImgWidth(e.target.value); setImgSize('width', e.target.value); }}
              className="w-12 text-xs border border-slate-300 rounded px-1 py-0.5 bg-white"
              title="Width (px)"
            />
            <span className="text-xs text-slate-400">×</span>
            <input
              type="number"
              placeholder="H"
              value={imgHeight}
              onChange={(e) => { setImgHeight(e.target.value); setImgSize('height', e.target.value); }}
              className="w-12 text-xs border border-slate-300 rounded px-1 py-0.5 bg-white"
              title="Height (px)"
            />
            <div className="w-px h-5 bg-slate-300 mx-0.5" />
            {btn('⬅', 'Align Left', () => alignImg('left'))}
            {btn('⬌', 'Align Center', () => alignImg('center'))}
            {btn('➡', 'Align Right', () => alignImg('right'))}
            <div className="w-px h-5 bg-slate-300 mx-0.5" />
            {btn('↺', 'Reset Image', resetImg)}
            {btn('🗑', 'Delete Image', deleteImg)}
          </div>
        )}
        <div className="w-px h-5 bg-slate-300 mx-1" />
        {btn('↶', 'Undo', () => exec('undo'))}
        {btn('↷', 'Redo', () => exec('redo'))}
        {btn('✕', 'Clear Formatting', () => exec('removeFormat'))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        onClick={handleEditorClick}
        data-placeholder={placeholder || 'Write your content here...'}
        className="px-4 py-3 min-h-[200px] max-h-[500px] overflow-y-auto text-slate-800 text-sm leading-relaxed focus:outline-none rich-text-content"
        style={{ wordBreak: 'break-word' }}
      />
      <style>{`
        .rich-text-content:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .rich-text-content h1 { font-size: 2em; font-weight: 700; margin: 0.5em 0; }
        .rich-text-content h2 { font-size: 1.5em; font-weight: 700; margin: 0.5em 0; }
        .rich-text-content h3 { font-size: 1.25em; font-weight: 600; margin: 0.5em 0; }
        .rich-text-content h4 { font-size: 1.1em; font-weight: 600; margin: 0.5em 0; }
        .rich-text-content h5 { font-size: 1em; font-weight: 600; margin: 0.5em 0; }
        .rich-text-content h6 { font-size: 0.9em; font-weight: 600; margin: 0.5em 0; }
        .rich-text-content p { margin: 0.5em 0; }
        .rich-text-content ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
        .rich-text-content ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        .rich-text-content blockquote { border-left: 3px solid #cbd5e1; padding-left: 1em; margin: 0.5em 0; color: #475569; font-style: italic; }
        .rich-text-content pre { background: #f1f5f9; padding: 0.75em; border-radius: 6px; font-family: monospace; overflow-x: auto; margin: 0.5em 0; }
        .rich-text-content a { color: #dc2626; text-decoration: underline; }
        .rich-text-content img { max-width: 100%; border-radius: 8px; margin: 0.5em 0; cursor: pointer; }
        .rich-text-content img:hover { outline: 2px solid #3b82f6; outline-offset: 2px; }
        .rich-text-content hr { border: none; border-top: 1px solid #e2e8f0; margin: 1em 0; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
